import React from 'react'
import { Bar } from '@nivo/bar'
import cn from 'classnames'

// @ts-ignore
import ArrowTop from 'app/components/SVGS/ArrowTop'
import { IBarChartGroupedHelper, IBarChartGroupedProps } from './interface'
// @ts-ignore
import TitleForRadarAndBar from '../TitleForRadarAndBar'
import { T1, T2 } from 'app/constants/reviews'
import { COLORS_BY_REVIEWER_TYPE, COLORS_FOR_RADAR_CHART_T2 } from '../../Reports'
import { NA, REVIEWER_RATINGS_TITLE } from '../../constants'

import './styles.scss'
export const bc = 'bar-chart-grouped'

const BarWrapper = (props: any) => <Bar {...props} />

export const addPercentageSymbol = (num: number): string => {
  if (num === 0) {
    return '0%'
  }
  return `${num < 0 ? '' : '+'}` + num.toFixed(1) + '%'
}

export const helper = ({ t1, t2 }: IBarChartGroupedHelper): string | number => {
  let res
  if (t1 === null || t2 === null) {
    res = NA
  } else if (t1 === 0) {
    res = t2 * 100
  } else {
    res = ((t2 - t1) / t1) * 100
  }
  return res
}

const renderDiff = (t1: number | null, t2: number | null, key: string, className?: string) => {
  const res = helper({ t1, t2 })
  return (
    <div
      key={`${bc}__row_${key}`}
      className={cn(`${bc}__res`, { [`${bc}__red`]: res < 0, [`${className}`]: className })}
    >
      {typeof res === 'number' ? addPercentageSymbol(res) : res}
    </div>
  )
}

const renderColumn = (data: any, property: any, barOptions: any) => {
  return (
    <div className={`${bc}__column ${bc}__reset-margin`}>
      <div className={`${bc}__avgs`} style={{ height: barOptions.axiosHeight }}>
        {data.map((el: any, i: number) => renderDiff(el[T1], el[T2], `${i}a`, `${bc}__avg`))}
      </div>
    </div>
  )
}

export const renderRow = (data: any) => {
  return (
    <div className={`${bc}__row ${bc}__row-t2`} style={{ padding: '0px 30px 0px 35px' }}>
      {data.map((el: any, i: number) => renderDiff(el[T1], el[T2], `${i}b`, `${bc}__avg-row`))}
    </div>
  )
}

const renderLeftAxios = (data: any, property: string, isBigChart: boolean | undefined = true, barOptions: any) => {
  return (
    <div className={cn(`${bc}__column`, { [`${bc}__column-big`]: isBigChart })}>
      <div className={`${bc}__avgs`} style={{ height: barOptions.axiosHeight }}>
        {data.map((el: any, i: number) => (
          <div key={`${property}_${i}`} className={`${bc}__row ${bc}__column-font`}>
            {isBigChart && (
              <div className={`${bc}__circle`} style={{ backgroundColor: COLORS_BY_REVIEWER_TYPE[el.reviewerType] }} />
            )}
            <div className={`${bc}__type`}>{el.reviewerType}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

const BarChartGrouped: React.FC<IBarChartGroupedProps> = ({
  title,
  classNameTitle,
  data,
  classNameWrapper,
  layout,
  isSmall = false,
  isRenderTitle,
}) => {
  const isLayoutHorizontal = layout === 'horizontal'
  const styleMapSmall = (isLayoutHorizontal: boolean) => ({
    // 1 must be never
    1: {
      barHeight: isLayoutHorizontal ? 75 : 430,
      axiosHeight: '65px',
      width: isLayoutHorizontal ? 320 : 500,
    },
    2: {
      barHeight: isLayoutHorizontal ? 130 : 430,
      axiosHeight: '105px',
      width: isLayoutHorizontal ? 320 : 500,
    },
    3: {
      barHeight: isLayoutHorizontal ? 185 : 430,
      axiosHeight: '145px',
      width: isLayoutHorizontal ? 320 : 500,
    },
    4: {
      barHeight: isLayoutHorizontal ? 240 : 430,
      axiosHeight: '205px',
      width: isLayoutHorizontal ? 320 : 500,
    },
  })
  const styleMapBig = {
    // 1 must be never
    1: {
      barHeight: 75,
      axiosHeight: '65px',
      width: 764,
    },
    2: {
      barHeight: 130,
      axiosHeight: '105px',
      width: 764,
    },
    3: {
      barHeight: 185,
      axiosHeight: '145px',
      width: 764,
    },
    4: {
      barHeight: 240,
      axiosHeight: '205px',
      width: 764,
    },
  }
  const barOptions = isSmall ? styleMapSmall(isLayoutHorizontal) : (styleMapBig as any)
  const keys = [T1, T2]
  const style = isLayoutHorizontal ? {} : { marginBottom: 0, padding: 0 }
  const compactedDataReversed = [...data].reverse()
  const dataLength = data.length as number
  return (
    <div className={cn(bc, { [`${classNameWrapper}`]: classNameWrapper })} style={style}>
      <div>
        <div className={`${bc}__title-wrapper`}>
          {title && (
            <span
              className={cn(`${bc}__title`, {
                // @ts-ignore
                [classNameTitle]: classNameTitle,
              })}
            >
              {title}
            </span>
          )}
          {isRenderTitle && (
            <TitleForRadarAndBar data={data} title={`${REVIEWER_RATINGS_TITLE}`} classNameTitle={classNameTitle} />
          )}
        </div>
        <div className={cn(`${bc}__chart`, { [`${bc}__chart-vertical`]: !isLayoutHorizontal })}>
          {isLayoutHorizontal && renderLeftAxios(data, 't1', !isSmall, barOptions[dataLength])}
          <BarWrapper
            maxValue={4}
            layout={layout}
            data={compactedDataReversed}
            width={barOptions[dataLength].width}
            height={barOptions[dataLength].barHeight}
            keys={keys}
            indexBy="reviewerType"
            margin={{ top: 5, right: 5, bottom: 25, left: 25 }}
            padding={0.28}
            colors={(node: any) => {
              return COLORS_FOR_RADAR_CHART_T2[node.id][node.indexValue]
            }}
            enableGridX={isLayoutHorizontal}
            enableGridY={!isLayoutHorizontal}
            enableLabel={!isLayoutHorizontal}
            axisBottom={{
              enable: true,
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
            }}
            axisLeft={isLayoutHorizontal ? null : {}}
            animate
            motionStiffness={90}
            motionDamping={15}
            groupMode="grouped"
            innerPadding={2}
          />
          {isLayoutHorizontal ? (
            <div className={`${bc}__columns`}>{renderColumn(data, T1, barOptions[dataLength])}</div>
          ) : (
            renderRow(compactedDataReversed)
          )}
        </div>
      </div>
    </div>
  )
}

export default BarChartGrouped
